import {
  AutomatonSectionContextMenuContext,
  AutomatonSectionContextMenuDynamicOptions,
  AutomatonSectionContextMenuOptions,
} from '@components/map/contextMenu/builders/automatonSection';
import { CommonMenuOptions } from '@components/map/contextMenu/builders/common';
import {
  PerimeterCameraContextMenuContext,
  PerimeterCameraContextMenuOptions,
} from '@components/map/contextMenu/builders/perimeterCamera';
import { PoiContextMenuContext, PoiContextMenuOptions } from '@components/map/contextMenu/builders/poi';
import {
  ReplayEventContextMenuContext,
  ReplayEventContextMenuDynamicOptions,
  ReplayEventContextMenuOptions,
} from '@components/map/contextMenu/builders/replayEvent';
import {
  SensorContextMenuContext,
  SensorContextMenuDynamicOptions,
  SensorContextMenuOptions,
} from '@components/map/contextMenu/builders/sensor';
import {
  TargetContextMenuContext,
  TargetContextMenuDynamicOptions,
  TargetContextMenuOptions,
} from '@components/map/contextMenu/builders/target';

import { FeatureTypeEnum } from '@/types/map.types';
import { MenuItemOption } from '@/types/menu.types';
import { SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

import {
  DefaultContextMenuContext,
  DefaultContextMenuDynamicOptions,
  DefaultContextMenuOptions,
} from './builders/default';

export enum FeatureTypeToContextComponentEnum {
  TARGET = 'TARGET',
  SENSOR = 'SENSOR',
  AUTOMATON_SECTION = 'AUTOMATON_SECTION',
  PERIMETER_CAMERA = 'PERIMETER_CAMERA',
  POI = 'POI',
  REPLAY_EVENT = 'REPLAY_EVENT',
  DEFAULT = 'DEFAULT',
}

export const featureTypeToContextComponentMap: Partial<Record<FeatureTypeEnum, FeatureTypeToContextComponentEnum>> = {
  [FeatureTypeEnum.TARGET_ICON]: FeatureTypeToContextComponentEnum.TARGET,
  [FeatureTypeEnum.SENSOR_ICON]: FeatureTypeToContextComponentEnum.SENSOR,
  [FeatureTypeEnum.SENSOR_LABEL]: FeatureTypeToContextComponentEnum.SENSOR,
  [FeatureTypeEnum.AUTOMATON_SECTION_ICON]: FeatureTypeToContextComponentEnum.AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_LINE]: FeatureTypeToContextComponentEnum.AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_LABEL]: FeatureTypeToContextComponentEnum.AUTOMATON_SECTION,
  [FeatureTypeEnum.AUTOMATON_SECTION_POINT]: FeatureTypeToContextComponentEnum.AUTOMATON_SECTION,
  [FeatureTypeEnum.PERIMETER_CAMERA_LABEL]: FeatureTypeToContextComponentEnum.PERIMETER_CAMERA,
  [FeatureTypeEnum.PERIMETER_CAMERA_SECTOR]: FeatureTypeToContextComponentEnum.PERIMETER_CAMERA,
  [FeatureTypeEnum.POI_ICON]: FeatureTypeToContextComponentEnum.POI,
  [FeatureTypeEnum.EVENT_ICON]: FeatureTypeToContextComponentEnum.REPLAY_EVENT,
};

type Keys<T> = T extends object ? keyof T : never;
export type MenuKey =
  | Keys<CommonMenuOptions>
  | Keys<TargetContextMenuOptions>
  | Keys<SensorContextMenuOptions>
  | Keys<AutomatonSectionContextMenuOptions>
  | Keys<PerimeterCameraContextMenuOptions>
  | Keys<PoiContextMenuOptions>
  | Keys<ReplayEventContextMenuOptions>
  | Keys<DefaultContextMenuOptions>;

type FeatureTypeContextMapType = {
  [FeatureTypeToContextComponentEnum.TARGET]: {
    ctx: TargetContextMenuContext;
    options: TargetContextMenuDynamicOptions;
  };
  [FeatureTypeToContextComponentEnum.SENSOR]: {
    ctx: SensorContextMenuContext<SensorTypeEnum | SubSensorTypeEnum | undefined>;
    options: SensorContextMenuDynamicOptions;
  };
  [FeatureTypeToContextComponentEnum.AUTOMATON_SECTION]: {
    ctx: AutomatonSectionContextMenuContext;
    options: AutomatonSectionContextMenuDynamicOptions;
  };
  [FeatureTypeToContextComponentEnum.PERIMETER_CAMERA]: {
    ctx: PerimeterCameraContextMenuContext;
    options: PerimeterCameraContextMenuOptions;
  };
  [FeatureTypeToContextComponentEnum.POI]: {
    ctx: PoiContextMenuContext;
    options: PoiContextMenuOptions;
  };
  [FeatureTypeToContextComponentEnum.REPLAY_EVENT]: {
    ctx: ReplayEventContextMenuContext;
    options: ReplayEventContextMenuDynamicOptions;
  };
  [FeatureTypeToContextComponentEnum.DEFAULT]: {
    ctx: DefaultContextMenuContext;
    options: DefaultContextMenuDynamicOptions;
  };
};

export type ContextOf<T extends FeatureTypeToContextComponentEnum> = T extends keyof FeatureTypeContextMapType
  ? FeatureTypeContextMapType[T]['ctx']
  : FeatureTypeContextMapType[FeatureTypeToContextComponentEnum.DEFAULT]['ctx'];
export type OptionsOf<T extends FeatureTypeToContextComponentEnum> = T extends keyof FeatureTypeContextMapType
  ? FeatureTypeContextMapType[T]['options']
  : FeatureTypeContextMapType[FeatureTypeToContextComponentEnum.DEFAULT]['options'];

interface MenuBuilderBase {
  order: number;
}

interface MenuItemBuilder<T extends FeatureTypeToContextComponentEnum> extends MenuBuilderBase {
  build: (ctx: ContextOf<T>) => [MenuKey, MenuItemOption];
  separator?: never;
}

interface SeparatorBuilder extends MenuBuilderBase {
  separator: true;
  build?: never;
}

export type MenuBuilder<T extends FeatureTypeToContextComponentEnum> = MenuItemBuilder<T> | SeparatorBuilder;
